import React, { useRef, useEffect, useState } from 'react';
import { useVideoStore } from '../store/videoStore';
import { useTimelineStore } from '../store/timelineStore';
import { useKeyboard } from '../hooks/useKeyboard';

// Electron IPC 通信
const { ipcRenderer } = window.require ? window.require('electron') : { ipcRenderer: null };

const VideoPlayer = () => {
  const videoRef = useRef(null);
  const [isRecording, setIsRecording] = useState({ offense: false, defense: false });
  const [recordingStart, setRecordingStart] = useState({ offense: null, defense: null });
  
  const {
    videoFile,
    isPlaying,
    currentTime,
    duration,
    playbackRate,
    setIsPlaying,
    setCurrentTime,
    setDuration,
    setPlaybackRate,
    togglePlay
  } = useVideoStore();

  const { addClip } = useTimelineStore();

  // 键盘快捷键处理
  useKeyboard({
    'Space': (e) => {
      e.preventDefault();
      togglePlay();
    },
    'KeyO': (e) => {
      e.preventDefault();
      handleOffenseClip();
    },
    'KeyD': (e) => {
      e.preventDefault();
      handleDefenseClip();
    },
    'ArrowLeft': (e) => {
      e.preventDefault();
      seekFrame(-1);
    },
    'ArrowRight': (e) => {
      e.preventDefault();
      seekFrame(1);
    }
  });

  // 处理进攻片段录制
  const handleOffenseClip = () => {
    if (!videoRef.current) return;
    
    const currentVideoTime = videoRef.current.currentTime;
    
    if (!isRecording.offense) {
      // 开始录制
      setIsRecording(prev => ({ ...prev, offense: true }));
      setRecordingStart(prev => ({ ...prev, offense: currentVideoTime }));
    } else {
      // 结束录制，创建片段
      const startTime = recordingStart.offense;
      const endTime = currentVideoTime;
      
      if (endTime > startTime) {
        addClip({
          type: 'offense',
          startTime,
          endTime,
          duration: endTime - startTime,
          title: `进攻片段 ${new Date().toLocaleTimeString()}`,
          description: ''
        });
      }
      
      setIsRecording(prev => ({ ...prev, offense: false }));
      setRecordingStart(prev => ({ ...prev, offense: null }));
    }
  };

  // 处理防守片段录制
  const handleDefenseClip = () => {
    if (!videoRef.current) return;
    
    const currentVideoTime = videoRef.current.currentTime;
    
    if (!isRecording.defense) {
      // 开始录制
      setIsRecording(prev => ({ ...prev, defense: true }));
      setRecordingStart(prev => ({ ...prev, defense: currentVideoTime }));
    } else {
      // 结束录制，创建片段
      const startTime = recordingStart.defense;
      const endTime = currentVideoTime;
      
      if (endTime > startTime) {
        addClip({
          type: 'defense',
          startTime,
          endTime,
          duration: endTime - startTime,
          title: `防守片段 ${new Date().toLocaleTimeString()}`,
          description: ''
        });
      }
      
      setIsRecording(prev => ({ ...prev, defense: false }));
      setRecordingStart(prev => ({ ...prev, defense: null }));
    }
  };

  // 逐帧播放
  const seekFrame = (direction) => {
    if (!videoRef.current) return;
    
    const frameRate = 30; // 假设30fps
    const frameTime = 1 / frameRate;
    const newTime = Math.max(0, Math.min(duration, currentTime + (direction * frameTime)));
    
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // 处理时间更新
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  // 处理视频加载
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      console.log('视频加载成功，时长:', videoRef.current.duration);
    }
  };

  // 处理视频加载错误
  const handleVideoError = (e) => {
    console.error('视频加载错误:', e.target.error);
    alert('视频加载失败，请检查文件格式是否支持');
  };

  // 处理视频可以播放
  const handleCanPlay = () => {
    console.log('视频可以播放');
  };

  // 拖拽处理函数
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    const videoFile = files.find(file => 
      file.type.startsWith('video/') || 
      /\.(mp4|mov|mkv|avi|wmv)$/i.test(file.name)
    );
    
    if (videoFile) {
      console.log('拖拽的视频文件:', videoFile.name);
      
      if (ipcRenderer) {
        // 在Electron环境中，获取文件路径
        const filePath = videoFile.path;
        console.log('视频文件路径:', filePath);
        useVideoStore.getState().setVideoFile(filePath);
      } else {
        // 在浏览器环境中，使用blob URL
        const fileUrl = URL.createObjectURL(videoFile);
        console.log('生成的视频URL:', fileUrl);
        useVideoStore.getState().setVideoFile(fileUrl);
        
        // 同时更新timelineStore中的videoFile，用于导出功能
        useTimelineStore.getState().setVideoFile(fileUrl);
      }
    } else {
      alert('请拖拽视频文件（支持MP4、MOV、MKV、AVI、WMV格式）');
    }
  };

  // 处理播放状态变化
  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying]);

  // 处理播放速度变化
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // 处理进度条拖拽
  const handleSeek = (e) => {
    if (!videoRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // 格式化时间显示
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const milliseconds = Math.floor((time % 1) * 1000);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  };

  // 打开文件对话框
  const handleOpenFile = async () => {
    if (!ipcRenderer) {
      console.error('Electron IPC not available');
      // 如果不在Electron环境中，使用HTML5文件选择
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'video/*';
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          const fileUrl = URL.createObjectURL(file);
          console.log('选择的视频文件:', file.name);
          console.log('生成的URL:', fileUrl);
          useVideoStore.getState().setVideoFile(fileUrl);
          
          // 同时更新timelineStore中的videoFile，用于导出功能
          useTimelineStore.getState().setVideoFile(fileUrl);
        }
      };
      input.click();
      return;
    }
    
    try {
      const result = await ipcRenderer.invoke('show-open-dialog', {
        properties: ['openFile'],
        filters: [
          { name: '视频文件', extensions: ['mp4', 'mov', 'mkv', 'avi', 'wmv'] }
        ]
      });
      
      if (!result.canceled && result.filePaths.length > 0) {
        const filePath = result.filePaths[0];
        console.log('选择的视频文件:', filePath);
        
        // 在Electron中，直接使用本地文件路径
        // 不需要file://协议，因为webSecurity已禁用
        console.log('设置视频文件路径:', filePath);
        useVideoStore.getState().setVideoFile(filePath);
        
        // 同时更新timelineStore中的videoFile，用于导出功能
        useTimelineStore.getState().setVideoFile(filePath);
      }
    } catch (error) {
      console.error('打开文件失败:', error);
      alert('打开文件失败: ' + error.message);
    }
  };

  return (
    <div className="video-player">
      {/* 控制栏 */}
      <div className="video-controls">
        <button onClick={handleOpenFile} className="btn-primary">
          打开视频
        </button>
        
        <button onClick={togglePlay} className="btn-control" disabled={!videoFile}>
          {isPlaying ? '⏸️' : '▶️'}
        </button>
        
        <select 
          value={playbackRate} 
          onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
          className="speed-select"
          disabled={!videoFile}
        >
          <option value={0.25}>0.25×</option>
          <option value={0.5}>0.5×</option>
          <option value={1}>1×</option>
          <option value={2}>2×</option>
          <option value={4}>4×</option>
          <option value={8}>8×</option>
        </select>

        <div className="recording-buttons">
          <button 
            onClick={handleOffenseClip}
            className={`btn-offense ${isRecording.offense ? 'recording' : ''}`}
            disabled={!videoFile}
          >
            [O] {isRecording.offense ? '结束进攻' : '进攻片段'}
          </button>
          
          <button 
            onClick={handleDefenseClip}
            className={`btn-defense ${isRecording.defense ? 'recording' : ''}`}
            disabled={!videoFile}
          >
            [D] {isRecording.defense ? '结束防守' : '防守片段'}
          </button>
        </div>
      </div>

      {/* 视频播放区域 */}
      <div className="video-container">
        {videoFile ? (
          <video
            ref={videoRef}
            src={videoFile}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onError={handleVideoError}
            onCanPlay={handleCanPlay}
            className="video-element"
            controls={false}
            preload="metadata"
          />
        ) : (
          <div 
            className="video-placeholder"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
          >
            <p>请打开视频文件开始剪辑</p>
            <p>支持拖拽视频文件到此处</p>
            <button onClick={handleOpenFile} className="btn-primary">
              选择视频文件
            </button>
          </div>
        )}
      </div>

      {/* 进度条 */}
      {videoFile && (
        <div className="progress-container">
          <div className="progress-bar" onClick={handleSeek}>
            <div 
              className="progress-fill"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
            
            {/* 录制标记 */}
            {isRecording.offense && recordingStart.offense !== null && (
              <div 
                className="recording-marker offense-marker"
                style={{ left: `${(recordingStart.offense / duration) * 100}%` }}
              />
            )}
            
            {isRecording.defense && recordingStart.defense !== null && (
              <div 
                className="recording-marker defense-marker"
                style={{ left: `${(recordingStart.defense / duration) * 100}%` }}
              />
            )}
          </div>
          
          <div className="time-display">
            <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;